import { currencies } from '@data/static/currencies';
import { FeatureFlag, FeatureFlagManager } from '@business/feature-flags';
/**
 * Fallback decimal scale used when a currency code is not found in the static
 * currency table. Matches the most common ISO 4217 minor-unit count.
 */
export const DEFAULT_CURRENCY_SCALE = 2;
// O(1) lookup of the static currency entry by code, built once at module load.
// getCurrencyScale runs in high-traffic formatting paths (useFormat, CartTotals,
// ShoppingCartCard), so we avoid scanning the full list on every call.
const currencyByCode = new Map(currencies.map((currency) => [currency.value, currency]));
/**
 * Single source of truth for how many decimals a currency displays, sourced
 * from the static currency table (`@data/static/currencies`). Zero-decimal
 * currencies (JPY, KRW, …) resolve to 0, 3-decimal currencies (JOD/KWD) to 3,
 * etc. Falls back to DEFAULT_CURRENCY_SCALE for unknown codes. (BENG-2731)
 * https://en.wikipedia.org/wiki/ISO_4217
 */
export const getCurrencyScale = (currency) => {
    var _a, _b;
    return (_b = (_a = currencyByCode.get(currency.toLowerCase())) === null || _a === void 0 ? void 0 : _a.decimalScale) !== null && _b !== void 0 ? _b : DEFAULT_CURRENCY_SCALE;
};
/**
 * Converts a minor-unit price to major units using the currency scale
 * (number of decimal places). e.g. USD (scale 2): 1005 -> 10.05;
 * JOD (scale 3): 34123 -> 34.123; JPY (scale 0): 171000 -> 171000.
 */
export const getFormattedPrice = (price, scale) => price / Math.pow(10, scale);
// Case-sensitive lookup (keys are lowercase), preserving the historical behavior.
export const getCurrencySymbol = (currencyCode) => { var _a; return ((_a = currencyByCode.get(currencyCode)) === null || _a === void 0 ? void 0 : _a.symbol) || ''; };
const currenciesValues = currencies.map((currency) => currency.value);
/**
 * Lives here (not in @data/types/common) so that merely validating a currency
 * does not drag the LaunchDarkly SDK into modules that only need common's light
 * language/locale helpers — notably the standalone payment page, whose i18n
 * bootstrap imports common (BENG-4169).
 */
export const isValidCurrency = (currency) => {
    const disabledCurrencies = [];
    if (FeatureFlagManager.isEnabled(FeatureFlag.disableBulgarianCurrency)) {
        disabledCurrencies.push('bgn');
    }
    return currenciesValues
        .filter((value) => !disabledCurrencies.includes(value))
        .includes(currency.toLowerCase());
};
